import { useCallback, useEffect, useState } from "react";
import { api, adminApi } from "@/lib/api";
import { Trash2, Plus, Save, Edit, X, LogOut, Image as ImageIcon } from "lucide-react";
import { RateEditor, DEFAULT_RATES, seedRowIds } from "@/components/admin/RateEditor";
import { PhotosManager } from "@/components/admin/PhotosManager";

const ADMIN_TOKEN_KEY = "beb_admin_token";

// sessionStorage clears on browser close — narrows XSS-token-persistence window
const tokenStore = {
  get: () => sessionStorage.getItem(ADMIN_TOKEN_KEY) || "",
  set: (t) => sessionStorage.setItem(ADMIN_TOKEN_KEY, t),
  clear: () => sessionStorage.removeItem(ADMIN_TOKEN_KEY),
};

const EMPTY = () => ({
  name: "",
  age: 24,
  price: 150,
  sort_order: 100,
  rates: seedRowIds(DEFAULT_RATES),
  nationality: "",
  hair: "Brunette",
  height: 170,
  measurements: "",
  languages: "English",
  services: "GFE, Dinner Date",
  restrictions: "",
  location: "Athens",
  incall: false,
  outcall: true,
  photos: [],
  cover_photo: "",
  description: "",
  verified: true,
  featured: false,
  is_active: true,
});

const toArray = (s) => {
  if (Array.isArray(s)) return s;
  return (s || "").split(",").map((x) => x.trim()).filter(Boolean);
};
const fromArray = (a) => (Array.isArray(a) ? a.join(", ") : a || "");

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

const toPayload = (form) => ({
  ...form,
  age: parseInt(form.age) || 0,
  price: parseInt(form.price) || 0,
  height: parseInt(form.height) || 170,
  sort_order: parseInt(form.sort_order) || 100,
  languages: toArray(form.languages),
  services: toArray(form.services),
  restrictions: toArray(form.restrictions),
  rates: (form.rates || [])
    .filter((r) => r && r.duration && r.price !== "" && r.price != null)
    .map((r) => ({ duration: String(r.duration).trim(), price: parseInt(r.price) || 0 })),
  photos: Array.isArray(form.photos) ? form.photos : toArray(form.photos),
});

const toForm = (c) => ({
  ...c,
  languages: fromArray(c.languages),
  services: fromArray(c.services),
  restrictions: fromArray(c.restrictions),
  rates: seedRowIds(
    Array.isArray(c.rates) && c.rates.length > 0
      ? c.rates.map((r) => ({ duration: r.duration || "", price: r.price ?? "" }))
      : DEFAULT_RATES
  ),
  photos: Array.isArray(c.photos) ? c.photos : toArray(c.photos),
});

const TEXT_FIELDS = [
  ["name", "Name"],
  ["sort_order", "Position (1 = first, 10 = last)", "number"],
  ["age", "Age", "number"],
  ["price", "Price (€/hr)", "number"],
  ["nationality", "Nationality"],
  ["hair", "Hair"],
  ["height", "Height (cm)", "number"],
  ["measurements", "Measurements"],
  ["location", "Location"],
  ["languages", "Languages (comma)"],
  ["services", "Services (comma)"],
  ["restrictions", "Restrictions (comma) — e.g. No kisses, No anal"],
];

const TOGGLES = [
  ["incall", "In-call"],
  ["outcall", "Out-call"],
  ["verified", "Verified"],
  ["featured", "Featured / VIP"],
  ["is_active", "Active"],
];

function LoginForm({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const { data } = await api.post("/admin/login", { password });
      tokenStore.set(data.token);
      onSuccess(data.token);
    } catch {
      setError("Invalid password");
    }
  };

  return (
    <div className="pt-40 px-6 md:px-12 min-h-screen" data-testid="admin-login-page">
      <form onSubmit={submit} className="max-w-md mx-auto border border-white/10 p-12 bg-[#0a0a0a]">
        <div className="label-luxury mb-4">Restricted Access</div>
        <h1 className="font-serif-display text-4xl mb-8">Admin Sign-In</h1>
        <input
          data-testid="admin-password"
          type="password"
          className="luxury-input"
          placeholder="Admin Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && <div className="text-red-400 text-sm mt-4 tracking-wider">{error}</div>}
        <button data-testid="admin-login-submit" type="submit" className="btn-gold btn-gold-solid mt-8 w-full">
          Enter
        </button>
      </form>
    </div>
  );
}

function CompanionForm({ form, setForm, onSave, onCancel, isCreating }) {
  const [uploading, setUploading] = useState(false);
  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  return (
    <div data-testid="admin-form" className="border border-[var(--gold)]/30 bg-[#0a0a0a] p-8 mb-10">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-serif-display text-3xl">
          {isCreating ? "New Companion" : `Editing: ${form.name}`}
        </h3>
        <button onClick={onCancel} className="text-white/50 hover:text-white">
          <X size={18} />
        </button>
      </div>

      <PhotosManager
        photos={form.photos}
        coverPhoto={form.cover_photo}
        onChange={updateForm}
        uploading={uploading}
        setUploading={setUploading}
      />

      <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
        {TEXT_FIELDS.map(([k, l, type]) => (
          <div key={k}>
            <div className="label-luxury mb-2">{l}</div>
            <input
              data-testid={`admin-field-${k}`}
              type={type || "text"}
              className="luxury-input"
              value={form[k] ?? ""}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            />
          </div>
        ))}
        <div className="md:col-span-2">
          <div className="label-luxury mb-2">Description</div>
          <textarea
            rows={3}
            className="luxury-input resize-none"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>

        <RateEditor rates={form.rates} onChange={(rates) => setForm((f) => ({ ...f, rates }))} />

        <div className="flex gap-8 md:col-span-2 flex-wrap">
          {TOGGLES.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm tracking-wider text-white/70">
              <input
                type="checkbox"
                checked={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
              />
              {l}
            </label>
          ))}
        </div>
      </div>
      <div className="mt-8 flex gap-3">
        <button onClick={onSave} data-testid="admin-save" className="btn-gold btn-gold-solid">
          <Save size={14} /> Save
        </button>
        <button onClick={onCancel} className="btn-gold">Cancel</button>
      </div>
    </div>
  );
}

function CompanionList({ companions, onEdit, onDelete }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {companions.map((c) => (
        <div key={c.id} className="border border-white/10 bg-[#0a0a0a] flex gap-4 p-4" data-testid={`admin-item-${c.id}`}>
          {c.cover_photo ? (
            <img src={resolveUrl(c.cover_photo)} alt="" className="w-24 h-32 object-cover" />
          ) : (
            <div className="w-24 h-32 bg-black flex items-center justify-center text-white/30">
              <ImageIcon size={28} />
            </div>
          )}
          <div className="flex-1 flex flex-col">
            <div className="font-serif-display text-xl">{c.name}</div>
            <div className="text-white/50 text-xs tracking-wider mt-1">
              {c.age} · {c.nationality} · €{c.price}
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              {c.featured && (
                <span className="text-[0.6rem] tracking-widest uppercase bg-[var(--gold)] text-black px-2 py-0.5">VIP</span>
              )}
              {!c.is_active && (
                <span className="text-[0.6rem] tracking-widest uppercase bg-red-900/50 text-red-300 px-2 py-0.5">Hidden</span>
              )}
            </div>
            <div className="flex-1" />
            <div className="flex gap-2 mt-3">
              <button data-testid={`admin-edit-${c.id}`} onClick={() => onEdit(c)} className="text-xs uppercase tracking-widest text-[var(--gold)] hover:underline">
                <Edit size={12} className="inline mr-1" /> Edit
              </button>
              <button data-testid={`admin-delete-${c.id}`} onClick={() => onDelete(c.id)} className="text-xs uppercase tracking-widest text-red-400 hover:underline">
                <Trash2 size={12} className="inline mr-1" /> Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ContactsList({ contacts }) {
  if (contacts.length === 0) {
    return <div className="text-white/40 label-luxury text-center py-20">No contact requests yet</div>;
  }
  return (
    <div className="space-y-3">
      {contacts.map((c) => (
        <div
          key={c.id || `${c.name}-${c.created_at || ""}`}
          className="border border-white/10 bg-[#0a0a0a] p-6"
          data-testid={`contact-item-${c.id || c.name}`}
        >
          <div className="flex justify-between mb-3">
            <div className="font-serif-display text-xl">{c.name}</div>
            <div className="text-white/40 text-xs tracking-wider">{c.phone || c.email}</div>
          </div>
          <p className="text-white/60 text-sm">{c.message}</p>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(tokenStore.get());
  const [companions, setCompanions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState("companions");

  const loggedIn = !!token;

  const refresh = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get("/companions"),
      adminApi().get("/admin/contacts").catch(() => ({ data: [] })),
    ]);
    setCompanions(a.data);
    setContacts(b.data);
  }, []);

  useEffect(() => {
    if (loggedIn) refresh();
  }, [loggedIn, refresh]);

  const logout = () => {
    tokenStore.clear();
    setToken("");
  };

  const startEdit = (c) => {
    setEditing(c.id);
    setForm(toForm(c));
    setCreating(false);
  };

  const startCreate = () => {
    setCreating(true);
    setEditing(null);
    setForm(EMPTY());
  };

  const cancel = () => {
    setEditing(null);
    setCreating(false);
    setForm(EMPTY());
  };

  const save = async () => {
    const payload = toPayload(form);
    if (!payload.cover_photo && payload.photos.length > 0) {
      payload.cover_photo = payload.photos[0];
    }
    try {
      if (creating) {
        await adminApi().post("/admin/companions", payload);
      } else {
        await adminApi().patch(`/admin/companions/${editing}`, payload);
      }
      cancel();
      refresh();
    } catch {
      alert("Failed to save");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this companion?")) return;
    await adminApi().delete(`/admin/companions/${id}`);
    refresh();
  };

  if (!loggedIn) return <LoginForm onSuccess={setToken} />;

  return (
    <div className="pt-28 pb-20 px-6 md:px-12" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="label-luxury mb-3">Concierge</div>
            <h1 className="font-serif-display text-5xl md:text-6xl">Admin Panel</h1>
          </div>
          <div className="flex gap-3">
            <button data-testid="admin-new" onClick={startCreate} className="btn-gold btn-gold-solid">
              <Plus size={14} /> New Companion
            </button>
            <button data-testid="admin-logout" onClick={logout} className="btn-gold">
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>

        <div className="flex gap-6 border-b border-white/10 mb-10">
          {["companions", "contacts"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`pb-4 text-xs uppercase tracking-[0.3em] ${
                tab === t ? "text-[var(--gold)] border-b border-[var(--gold)]" : "text-white/50"
              }`}
            >
              {t} {t === "companions" ? `(${companions.length})` : `(${contacts.length})`}
            </button>
          ))}
        </div>

        {(editing || creating) && (
          <CompanionForm
            form={form}
            setForm={setForm}
            onSave={save}
            onCancel={cancel}
            isCreating={creating}
          />
        )}

        {tab === "companions" && (
          <CompanionList companions={companions} onEdit={startEdit} onDelete={remove} />
        )}

        {tab === "contacts" && <ContactsList contacts={contacts} />}
      </div>
    </div>
  );
}
